<template>
  <div class="app-container my-4">
    <div class="row" dir="rtl">


      <!-- Calendar Column -->
      <div :class="isFullScreen ? 'col-12' : 'col-md-9'">
        <div class="calendar-container">
          <!-- Only display compact calendar when not in fullscreen -->
          <div v-if="!isFullScreen">
            <!-- Month Navigation Header -->
            <div class="d-flex justify-content-between align-items-center px-3 py-2 calendar-header my-5">
              <div class="nav-arrow" @click="prevMonth">
                <i class="fas fa-chevron-right"></i>
              </div>
              <div class="month-header text-center flex-grow-1">
                {{ currentMonthName }} {{ currentYear }}
              </div>
              <div class="d-flex align-items-center">
                <div class="nav-arrow" @click="nextMonth">
                  <i class="fas fa-chevron-left"></i>
                </div>
              </div>
            </div>

            <!-- Compact Calendar Grid View -->
            <div class="compact-calendar px-2">
              <div class="row text-center mb-2">
                <div class="col" v-for="day in weekDays" :key="day">{{ day }}</div>
              </div>
              <div class="row mb-2" v-for="(week, weekIndex) in calendarDays" :key="weekIndex">
                <div class="col p-0" v-for="(day, dayIndex) in week" :key="dayIndex">
                  <div class="day-cell d-flex flex-column position-relative"
                    :class="{ 'today': isToday(day), 'other-month': !isSameMonth(day) }" @click="selectDay(day)">
                    <div class="day-number">{{ day.getDate() }}</div>
                    <div v-if="getEventCount(day) > 0" class="day-badge badge position-absolute">
                      {{ getEventCount(day) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- FullCalendar in both views -->
          <div v-else :class="{ 'fullscreen-calendar': isFullScreen }">
            <div id="calendar" ref="calendarEl"></div>
            <!-- <div class="text-center my-5">
              <button class="btn btn-outline-secondary" @click="toggleFullScreen">
                <i :class="isFullScreen ? 'fas fa-compress' : 'fas fa-expand'"></i>
                {{ isFullScreen ? 'العودة' : 'شاشة كاملة' }}
              </button>
            </div> -->
          </div>

          <!-- Toggle Fullscreen Button -->
          <div class="row">
            <div class="col-12 text-center my-5">
              <button class="btn btn-outline-secondary" @click="toggleFullScreen">
                <i :class="isFullScreen ? 'fas fa-compress' : 'fas fa-expand'"></i>
                {{ isFullScreen ? 'العودة' : 'شاشة كاملة' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Cards (Visible only in regular view) -->
      <div class="col-md-3" v-if="!isFullScreen">
        <div class="stat-card text-center mb-4">
          <div class="stat-label text-teal">الطلبات الحالية</div>
          <div class="text-teal stat-value">{{ totalRequests }}</div>
        </div>
        <div class="stat-card text-center mb-4">
          <div class="stat-label text-danger">الطلبات المتأخرة</div>
          <!-- <div class="text-danger stat-value">{{ pendingRequests }}</div> -->
          <div class="text-danger stat-value" @click="toggleDelayedRequests" style="cursor: pointer;">{{ pendingRequests
          }}</div>
        </div>

        <div v-if="showDelayedRequests">
          <div class="request-item" v-for="request in DelayedRequests" :key="request.id" @click="openDialog(request)"
            style="cursor: pointer">
            <div class="request-status" :class="getStatusClass(request.color)"></div>
            <div class="flex-grow-1">
              <div class="fw-bold">{{ request.customer_name }}</div>
              <div class="text-muted small">{{ request.customer_phone }}</div>
            </div>
            <div class="text-muted small">{{ request.time }}</div>
          </div>
        </div>


        <h5 class="mb-3">طلبات اليوم</h5>
        <div class="requests-section">
          <div class="request-item" v-for="request in todayRequests" :key="request.id" @click="openDialog(request)"
            style="cursor: pointer">
            <div class="request-status" :class="getStatusClass(request.color)"></div>
            <div class="flex-grow-1">
              <div class="fw-bold">{{ request.customer_name }}</div>
              <div class="text-muted small">{{ request.customer_phone }}</div>
            </div>
            <div class="text-muted small">{{ request.time }}</div>
          </div>
        </div>



        <h5 class="mb-3">طلبات عاجلة</h5>
        <div class="requests-section">
          <div class="request-item" v-for="request in urgentRequests" :key="request.id" @click="openDialog(request)"
            style="cursor: pointer">
            <div class="request-status" :class="getStatusClass('#dc3545')"></div>
            <div class="flex-grow-1">
              <div class="fw-bold">{{ request.customer_name }}</div>
              <div class="text-muted small">{{ request.customer_phone }}</div>
            </div>
            <div class="text-muted small">{{ request.time }}</div>
          </div>
        </div>
      </div>


    </div>

    <!-- User Detail Popup -->
    <div v-if="showUserDetail">
      <div class="overlay" @click="showUserDetail = false"></div>
      <div class="user-detail-popup">
        <h5 class="mb-3">اسم العميل: {{ selectedUser.customer_name }}</h5>
        <p>رقم الجوال: {{ selectedUser.customer_phone }}</p>
        <button class="btn btn-purple w-100" @click="openUserFile(selectedUser)">فتح الملف</button>
      </div>
    </div>
  </div>
</template>

<script>
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
// import interactionPlugin from '@fullcalendar/interaction';
import Axios from 'axios';

export default {
  name: 'ImprovedCalendar',
  props: {
    initialEvents: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // Auth data
      currentRole: 0,
      theCookie: document.cookie
        .split(";")
        .map((coki) => coki.split("="))
        .reduce(
          (acc, [key, val]) => ({
            ...acc,
            [key.trim()]: decodeURIComponent(val),
          }),
          {}
        )["wsataJwt"],

      // Calendar state
      calendar: null,
      currentDate: new Date(),
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      selectedDate: new Date(),
      isFullScreen: false,
      weekDays: ['السبت', 'الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة'],
      monthNames: ['يناير', 'فبراير', 'مارس', 'إبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],

      // User detail popup
      showUserDetail: false,
      selectedUser: {},

      // Sample data - replace with your actual data from API
      events: [],

      todayRequests: [],

      urgentRequests: [],

      totalRequests: 0,
      pendingRequests: 0,

      DelayedRequests: [],
      showDelayedRequests: false,


      // Modal dialog state
      dialog: false,
      loading: false,
      hoveredData: {
        name: "",
        phone: "",
      },
      id: "",
    };
  },
  computed: {
    currentMonthName() {
      return this.monthNames[this.currentMonth];
    },

    daysInMonth() {
      return new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
    },

    firstDayOfMonth() {
      // return new Date(this.currentYear, this.currentMonth, 1).getDay();
      const day = new Date(this.currentYear, this.currentMonth, 1).getDay();
      return day + 1;
    },

    calendarDays() {
      const days = [];
      let week = [];

      // Previous month days
      const firstDay = this.firstDayOfMonth;
      const prevMonthLastDay = new Date(this.currentYear, this.currentMonth, 0).getDate();

      for (let i = firstDay - 1; i >= 0; i--) {
        const day = new Date(this.currentYear, this.currentMonth - 1, prevMonthLastDay - i);
        week.push(day);
      }

      // Current month days
      for (let i = 1; i <= this.daysInMonth; i++) {
        const day = new Date(this.currentYear, this.currentMonth, i);
        week.push(day);

        if (week.length === 7) {
          days.push(week);
          week = [];
        }
      }

      // Next month days
      if (week.length > 0) {
        const daysNeeded = 7 - week.length;
        for (let i = 1; i <= daysNeeded; i++) {
          const day = new Date(this.currentYear, this.currentMonth + 1, i);
          week.push(day);
        }
        days.push(week);
      }

      return days;
    }
  },
  watch: {
    // Update the calendar when switching between fullscreen and compact view
    isFullScreen(newVal) {
      this.$nextTick(() => {
        this.destroyCalendar();
        this.initCalendar();
      });
    }
  },
  async mounted() {
    await this.getData();
    this.initCalendar();
  },
  methods: {

    toggleDelayedRequests() {
      this.showDelayedRequests = !this.showDelayedRequests;
    },
    initCalendar() {
      console.log('event is ');
      console.log(this.events);
      // Transform events to FullCalendar format
      const calendarEvents = this.events.map(event => ({
        id: event.id,
        title: event.title,
        start: event.start,
        color: event.color,
        extendedProps: { time: event.time },
        customer_name: event.customer_name,
        customer_phone: event.customer_phone,
        status: event.status,
        statusClass: this.getStatusClass(event.status)
      }));

      console.log('calendarEvents');
      console.log(calendarEvents);


      const calendarEl = document.getElementById('calendar');

      this.calendar = new Calendar(calendarEl, {
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin],
        headerToolbar: this.isFullScreen ? {
          left: 'today prev,next',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
        } : {
          left: '',
          center: '',
          right: ''
        },
        initialView: this.isFullScreen ? 'dayGridMonth' : 'dayGridMonth',
        locale: 'ar',
        direction: 'rtl',
        height: this.isFullScreen ? 'auto' : 300,
        events: calendarEvents,
        navLinks: true,
        editable: false,
        selectable: true,
        slotMinTime: "07:00:00",
        slotMaxTime: "19:00:00",
        dayCellClassNames: (arg) => {
          const classes = [];
          if (this.isToday(arg.date)) classes.push('today');
          return classes;
        },
        eventClick: async (info) => {
          await this.handleEventClick(info.event);
        },
        dateClick: (info) => {
          this.selectDay(info.date);
        },

        /*eventContent: function (arg) {
          const customerName = arg.event.extendedProps.customer_name;
          const customerPhone = arg.event.extendedProps.customer_phone;
          const status = arg.event.extendedProps.status;
          const time = arg.event.extendedProps.time;
          const statusClass = arg.event.extendedProps.statusClass;
          const color = arg.event.backgroundColor || arg.event.color;

          return {
            html: `
              <div class="fc-custom-event" style="
                border: 1px solid #dee2e6;
                border-radius: 8px;
                padding: 4px;
                background-color: white;
                cursor: pointer;
                margin-bottom: 4px;
              ">
                <div class="d-flex align-items-center gap-2">
                  <div class="request-status ${statusClass}"></div>
                  <div class="flex-grow-1">
                    <div class="fw-bold">${customerName}</div>
                    <div class="text-muted small">${customerPhone}</div>
                  </div>
                  <div class="text-muted small">${time}</div>
                </div>
              </div>
            `
          };
        },*/

        eventDidMount: function (info) {
          const el = info.el;
          const props = info.event.extendedProps;
          el.innerHTML = `
              <div class="fc-custom-event" style="
                background-color: white;
                border: 1px solid #dee2e6;
                border-radius: 8px;
                padding: 4px;
                margin-bottom: 4px;
                cursor: pointer;
              ">
                <div class="d-flex align-items-center gap-2">
                  <div style="
                    background-color: ${info.backgroundColor};
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                  "></div>
                  <div class="flex-grow-1">
                    <div class="fw-bold">${props.customer_name}</div>
                    <div class="text-muted small">${props.customer_phone}</div>
                  </div>
                  <div class="text-muted small">${props.time}</div>
                </div>
              </div>
            `;
        },

        buttonText: {
          today: 'اليوم',
          month: 'شهر',
          week: 'اسبوع',
          day: 'يوم',
          list: 'قائمة'
        }
      });

      this.calendar.render();
    },

    destroyCalendar() {
      if (this.calendar) {
        this.calendar.destroy();
        this.calendar = null;
      }
    },

    async getData() {
      this.loading = true;
      // Check if we have token before making API call
      if (!this.theCookie) {
        console.error('No authentication token found');
        this.loading = false;
        return;
      }

      try {
        // Parse the cookie only if it exists
        const token = JSON.parse(this.theCookie)?.token;
        this.currentRole = JSON.parse(this.theCookie)?.role;

        if (!token) {
          console.error('Invalid token format');
          this.loading = false;
          return;
        }

        const response = await Axios.get(`${this.$store.state.url}/agent/Reminders`, {
          headers: {
            "x-api-key": "WFKvB4Vjr8v5BVmxY7CDQ6ZMAU8DSHSBFKCMVpkfd5hhZyKTvnV3uh5XwxSwhnfeUbptZ7Z4XFJCR4vKXeHWGHTZ6djsYXjdAatf",
            Authorization: `Bearer ${token}`
          }
        });

        // const data = response.data.payload;
        const data = response.data.payload.reminders;
        this.totalRequests = response.data.payload.current_reminders_count;
        this.pendingRequests = response.data.payload.overdue_reminders_count;

        // this.todayRequests = response.data.payload.today_reminders;
        // this.urgentRequests = response.data.payload.urgent_reminders;


        // Process events
        data.forEach(rem => {
          let color = "";
          if (rem.status == 0) {
            color = "#F4516C"; // Red
          } else if (rem.status == 1) {
            color = "#25d366"; // Green
          } else {
            color = "#3788D8"; // Blue
          }

          const event = {
            id: rem.req_id,
            title: rem.value,
            start: new Date(rem.reminder_date),
            color: rem.color || color,
            time: this.formatEventTime(new Date(rem.reminder_date)),
            customer_name: rem.customer_name,
            customer_phone: rem.customer_phone,
          };

          this.events.push(event);
        });

        response.data.payload.today_reminders.forEach(rem => {
          let color = "";
          if (rem.status == 0) {
            color = "#F4516C"; // Red
          } else if (rem.status == 1) {
            color = "#25d366"; // Green
          } else {
            color = "#3788D8"; // Blue
          }

          const event = {
            id: rem.req_id,
            title: rem.value,
            start: new Date(rem.reminder_date),
            color: rem.color || color,
            time: this.formatEventTime(new Date(rem.reminder_date)),
            customer_name: rem.customer_name,
            customer_phone: rem.customer_phone,
          };

          this.todayRequests.push(event);
        });

        response.data.payload.urgent_reminders.forEach(rem => {
          let color = "";
          if (rem.status == 0) {
            color = "#F4516C"; // Red
          } else if (rem.status == 1) {
            color = "#25d366"; // Green
          } else {
            color = "#3788D8"; // Blue
          }

          const event = {
            id: rem.req_id,
            title: rem.value,
            start: new Date(rem.reminder_date),
            color: rem.color || color,
            time: this.formatEventTime(new Date(rem.reminder_date)),
            customer_name: rem.customer_name,
            customer_phone: rem.customer_phone,
          };

          this.urgentRequests.push(event);
        });

        response.data.payload.overdueReminders.forEach(rem => {
          let color = "";
          if (rem.status == 0) {
            color = "#F4516C"; // Red
          } else if (rem.status == 1) {
            color = "#25d366"; // Green
          } else {
            color = "#3788D8"; // Blue
          }

          const event = {
            id: rem.req_id,
            title: rem.value,
            start: new Date(rem.reminder_date),
            color: rem.color || color,
            time: rem.reminder_date,
            // time: this.formatEventTime(new Date(rem.reminder_date)),
            customer_name: rem.customer_name,
            customer_phone: rem.customer_phone,
          };

          this.DelayedRequests.push(event);
        });

        // Update the calendar with new events
        if (this.calendar) {
          this.events.forEach(event => {
            this.calendar.addEvent({
              id: event.id,
              title: event.title,
              start: event.start,
              color: event.color,
              extendedProps: { time: event.time }
            });
          });
        }
      } catch (error) {
        console.error('Failed to fetch calendar data:', error);
      } finally {
        this.loading = false;
      }
    },

    formatEventTime(date) {
      // Format the time in Arabic (e.g., "8:27 ص")
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'م' : 'ص';
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;

      return `${formattedHours}:${formattedMinutes} ${ampm}`;
    },

    async handleEventClick(event) {
      // Display event details or open user detail popup
      console.log(event)
      this.selectedUser = {
        id: event.id,
        title: event.title,
        start: new Date(event.start),
        color: event.color,
        customer_name: event.extendedProps.customer_name,
        customer_phone: event.extendedProps.customer_phone,
        time: event.extendedProps.time,
      };
      this.showUserDetail = true;

      // Fetch additional details if needed
      await this.hoverEvent(event.id);
    },

    openDialog(request) {
      this.selectedUser = request;
      this.showUserDetail = true;
    },

    async hoverEvent(id) {
      this.dialog = true;
      this.loading = true;

      try {
        const response = await Axios.get(
          `${this.$store.state.url}/v2/employee/Requests/${id}`,
          {
            headers: this.request_headers()
          }
        );

        this.hoveredData.name = response.data.payload.customer.name;
        this.hoveredData.phone = response.data.payload.customer.mobile;
        this.id = id;
      } catch (error) {
        this.dialog = false;
        this.showPopup("error", error.response?.data?.message || "حدث خطأ");
      } finally {
        this.loading = false;
      }
    },

    request_headers() {
      // Implement your headers logic here
      return {
        "x-api-key": "WFKvB4Vjr8v5BVmxY7CDQ6ZMAU8DSHSBFKCMVpkfd5hhZyKTvnV3uh5XwxSwhnfeUbptZ7Z4XFJCR4vKXeHWGHTZ6djsYXjdAatf",
        Authorization: `Bearer ${JSON.parse(this.theCookie)?.token}`
      };
    },

    showPopup(type, message) {
      // Implement your popup logic here or use a toast notification
      console.log(`${type}: ${message}`);
    },

    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentYear--;
        this.currentMonth = 11;
      } else {
        this.currentMonth--;
      }
      this.selectedDate = new Date(this.currentYear, this.currentMonth, 1);
      this.updateCalendar();
    },

    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentYear++;
        this.currentMonth = 0;
      } else {
        this.currentMonth++;
      }
      this.selectedDate = new Date(this.currentYear, this.currentMonth, 1);
      this.updateCalendar();
    },

    updateCalendar() {
      if (this.calendar) {
        this.calendar.gotoDate(this.selectedDate);
      }
    },

    selectDay(day) {
      this.selectedDate = new Date(day);

      // Update the selected date in the calendar
      if (this.calendar) {
        this.calendar.gotoDate(this.selectedDate);
      }

      // Update stats based on selected day
      // this.updateDayStats(day);

      // Emit event to parent component if needed
      this.$emit('day-selected', this.selectedDate);
    },

    isToday(date) {
      const today = new Date();
      return date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();
    },

    isSameMonth(date) {
      return date.getMonth() === this.currentMonth;
    },

    getEvents(day) {
      return this.events.filter(event =>
        event.start.getDate() === day.getDate() &&
        event.start.getMonth() === day.getMonth() &&
        event.start.getFullYear() === day.getFullYear()
      );
    },

    getEventCount(day) {
      return this.getEvents(day).length;
    },

    updateDayStats(day) {
      // Update today's requests based on selected day
      const events = this.getEvents(day);

      if (events.length > 0) {
        this.todayRequests = events.map(event => ({
          id: event.id,
          name: event.title,
          time: event.time,
          statusClass: this.getStatusClass(event.color)
        })).slice(0, 3);
      }
    },

    getStatusClass(color) {
      // Map color to status class
      const colorMap = {
        '#0d6efd': 'status-blue',
        '#20c997': 'status-teal',
        '#dc3545': 'status-red',
        '#fd7e14': 'status-orange',
        '#F4516C': 'status-red',
        '#25d366': 'status-teal',
        '#3788D8': 'status-blue'
      };

      return colorMap[color] || 'status-blue';
    },

    toggleFullScreen() {
      this.isFullScreen = !this.isFullScreen;
    },

    openUserFile(selectedUser) {
      this.showUserDetail = false;
      if (this.currentRole == 5) {
        console.log('enter quality !!!!!')
        this.$router.push({
          name: "quality_request_details",
          params: { id: selectedUser.id },
        });
      } else {
        console.log('enter else  !!!!!')
        this.$router.push({
          name: "request_details",
          params: { id: selectedUser.id },
        });
      }

      // this.$emit('open-user-file', this.selectedUser);
    }
  },
  beforeDestroy() {
    this.destroyCalendar();
  }
};
</script>

<style scoped>
.app-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
}

.calendar-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 15px;
}

/* Month header styling */
.month-header {
  color: #5e0a78;
  font-weight: bold;
  font-size: 1.5rem;
}

.nav-arrow {
  background-color: #5e0a78;
  color: white;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
}

/* Compact calendar styling */
.compact-calendar {
  margin-bottom: 20px;
}

.day-cell {
  height: 60px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin: 2px;
  padding: 5px;
  background-color: white;
  cursor: pointer;
  transition: background-color 0.2s;
}

.day-cell:hover {
  background-color: #f8f9fa;
}

.day-cell.today {
  background-color: #e8f4ff;
}

.day-cell.other-month {
  opacity: 0.5;
}

.day-number {
  font-size: 16px;
  font-weight: bold;
  text-align: center;
}

.day-badge {
  background-color: #5e0a78 !important;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

/* Stats and requests styling */
.stat-card {
  background-color: white;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
}

.text-teal {
  color: #20c997;
}

.text-danger {
  color: #dc3545;
}

.stat-label {
  color: #555;
  font-size: 1rem;
}

.requests-section {
  background-color: white;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.request-item {
  padding: 10px 0;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
}

.request-status {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-left: 10px;
}

.status-red {
  background-color: #dc3545;
}

.status-blue {
  background-color: #0d6efd;
}

.status-teal {
  background-color: #20c997;
}

.status-orange {
  background-color: #fd7e14;
}

/* Fullscreen calendar styling */
.fullscreen-calendar {
  height: 100vh;
  width: 100%;
}

/* User detail popup */
.user-detail-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  width: 300px;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.btn-purple {
  background-color: #5e0a78;
  color: white;
}

.btn-purple:hover {
  background-color: #4a0861;
  color: white;
}

/* Overrides for FullCalendar */
:deep(.fc) {
  direction: rtl;
}

:deep(.fc-toolbar-title) {
  color: #5e0a78;
  font-weight: bold;
}

:deep(.fc-button-primary) {
  background-color: #5e0a78;
  border-color: #5e0a78;
}

:deep(.fc-button-primary:hover) {
  background-color: #4a0861;
  border-color: #4a0861;
}

:deep(.fc-day-today) {
  background-color: #e8f4ff !important;
}

:deep(.fc-event) {
  border-radius: 4px;
  padding: 2px;
  cursor: pointer;
}



.requests-section {
  background-color: white;
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  direction: rtl;
}

h5 {
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 18px;
  color: #333;
  font-weight: 600;
}

.request-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.request-item:last-child {
  border-bottom: none;
}

.request-status {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-left: 10px;
}

.status-pending {
  background-color: #dc3545;
}

.status-completed {
  background-color: #28a745;
}

.status-cancelled {
  background-color: #6c757d;
}

.flex-grow-1 {
  flex-grow: 1;
}

.fw-bold {
  font-weight: bold;
}

.text-muted {
  color: #6c757d;
}

.small {
  font-size: 0.875em;
}

.mb-3 {
  margin-bottom: 1rem;
}

.fc-event-title {
  white-space: normal !important;
  overflow-wrap: break-word;
}

.day-cell {
  border: none !important;
}

.day-cell {
  position: relative;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.day-number {
  font-size: 16px;
  font-weight: bold;
}

.day-badge {
  background-color: #5e0a78 !important;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 10;
}


:deep(.fc-daygrid-day-events) {
  max-height: 100px;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100% !important;
  box-sizing: border-box;
}
</style>